import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { TripState } from "../types";

export default function FleetSummary() {
  const trips = useSelector((s: RootState) => s.fleet.trips);

  const stats = useMemo(() => {
    const all: TripState[] = Object.values(trips ?? {});
    const started = all.filter((t) => t?.started);

    const completed = started.filter(
      (t) => t.status === "completed" || (t.progress ?? 0) >= 100
    ).length;
    const overspeed = started.filter((t) => (t.overspeedCount ?? 0) > 0).length;
    const morestops = started.filter((t) => t.stops > 1).length;
    const gte50 = started.filter((t) => (t.progress ?? 0) >= 50).length;
    const inIssue = started.filter((t) => t.status === "issue").length;
    const cancelled = started.filter((t) => t.status === "cancelled").length;
    const lowFuel = started.filter(
      (t) => typeof t.fuel === "number" && (t.fuel as number) < 50
    ).length;

    return {
      completed,
      gte50,
      inIssue,
      cancelled,
      lowFuel,
      overspeed,
      morestops,
    };
  }, [trips]);

  const Card = ({
    tone,
    label,
    value,
    infoTitle,
  }: {
    tone: "green" | "red" | "yellow" | "slate";
    label: string;
    value: number;
    infoTitle?: string;
  }) => {
    const toneBg = {
      green: "bg-[#ecfdf3]",
      red: "bg-[#feecec]",
      yellow: "bg-[#fff7e6]",
      slate: "bg-[#eff4ff]",
    }[tone];

    return (
      <div className={`flex items-center justify-center gap-3 py-[14px] px-4 rounded-[14px] shadow-[0_2px_10px_rgba(16,24,40,0.04)] ${toneBg}`}>
        <div className="grid gap-[2px]">
          <div className="inline-flex items-center gap-[6px] text-[#0f172a] text-[13px] font-semibold">
            {label}
            <div className="relative inline-grid place-items-center w-4 h-4 rounded-full bg-[rgba(15,23,42,0.08)] text-[#0f172a] text-[11px] font-bold cursor-pointer select-none group">
              i
              {infoTitle && (
                <div className="absolute bottom-[120%] left-1/2 -translate-x-1/2 bg-[#1e2f8a] text-white px-2 py-[6px] rounded-[6px] text-[12px] leading-[1.2] whitespace-nowrap opacity-0 pointer-events-none transition-transform duration-150 ease-linear group-hover:opacity-100 group-hover:-translate-y-[2px] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[5px] after:border-solid after:border-t-[#1e2f8a] after:border-x-transparent after:border-b-transparent">
                  {infoTitle}
                </div>
              )}
            </div>
          </div>
          <div className="text-[#0b1220] text-[24px] font-bold leading-[1.1]">
            {value.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="grid [grid-template-columns:repeat(auto-fit,minmax(120px,1fr))] gap-2 mt-[10px] mb-4">
      <Card
        tone="slate"
        label="≥ 50% Complete"
        value={stats.gte50}
        infoTitle="Trips that completed 50% of their duration"
      />
      <Card
        tone="slate"
        label="Fuel < 50%"
        value={stats.lowFuel}
        infoTitle="Trips whose latest fuel reading is below 50%"
      />
      <Card
        tone="slate"
        label="Overspeed Trips"
        value={stats.overspeed}
        infoTitle="Trips that have overspeed events"
      />
      <Card
        tone="slate"
        label=">1 stops"
        value={stats.morestops}
        infoTitle="Trips that have more than 1 stop"
      />
      <Card
        tone="yellow"
        label="Trips in Issue"
        value={stats.inIssue}
        infoTitle="Trips flagged with issues or warnings"
      />
      <Card
        tone="red"
        label="Cancelled Trips"
        value={stats.cancelled}
        infoTitle="Trips that were cancelled"
      />
      <Card
        tone="green"
        label="Completed Trips"
        value={stats.completed}
        infoTitle="Trips that are completed"
      />
    </section>
  );
}
